<template>
  <div>
    <!-- KgDM Table (Pit silage only) -->
    <div class="row">
      <div class="col-md-12 text-left">
        <h3 class="h3 g-color-black text-uppercase mb-2">KgDM</h3>
      </div>
    </div>

    <div class="row">
      <div class="col-md-12">
        <!-- Table Bordered -->
        <div class="table-responsive">
          <table class="table table-bordered">
            <thead>
            <tr>
              <th>Feed Type</th>
              <th>Opening Balance</th>
              <th>Made On Farm</th>
              <th>Purchased</th>
              <th>Sold</th>
              <th>Fed</th>
              <th>Unexplained Variance</th>
              <th>Closing Balance</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="feedType in pitSilageFeedTypes" v-bind:key="feedType.value">
              <td>{{ getFeedType(feedType.value) }}</td>
              <td>{{ numberUtils.formatNumberWithCommas(getTransaction(feedType.value).openingBalance) }}</td>
              <td>{{ numberUtils.formatNumberWithCommas(getTransaction(feedType.value).madeOnFarm) }}</td>
              <td>{{ numberUtils.formatNumberWithCommas(getTransaction(feedType.value).purchased) }}</td>
              <td>{{ numberUtils.formatNumberWithCommas(getTransaction(feedType.value).sold) }}</td>
              <td>{{ numberUtils.formatNumberWithCommas(getTransaction(feedType.value).fed) }}</td>
              <td>{{ numberUtils.formatNumberWithCommas(getTransaction(feedType.value).unexplainedVariance) }}</td>
              <td :class="highlightNegativeError && getTransaction(feedType.value).closingBalance < 0 ? 'g-color-red' : ''"
                  class="g-font-weight-900">{{ numberUtils.formatNumberWithCommas(getTransaction(feedType.value).closingBalance) }}
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Bales Table (All other feed types) -->
    <div class="row mt-4">
      <div class="col-md-12 text-left">
        <h3 class="h3 g-color-black text-uppercase mb-2">Bales</h3>
      </div>
    </div>

    <div class="row">
      <div class="col-md-12">
        <!-- Table Bordered -->
        <div class="table-responsive">
          <table class="table table-bordered">
            <thead>
            <tr>
              <th>Feed Type</th>
              <th>Opening Balance</th>
              <th>Made On Farm</th>
              <th>Purchased</th>
              <th>Sold</th>
              <th>Fed</th>
              <th>Unexplained Variance</th>
              <th>Closing Balance</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="feedType in otherFeedTypes" v-bind:key="feedType.value">
              <td>{{ getFeedType(feedType.value) }}</td>
              <td>{{ numberUtils.formatNumberWithCommas(getTransaction(feedType.value).openingBalance) }}</td>
              <td>{{ numberUtils.formatNumberWithCommas(getTransaction(feedType.value).madeOnFarm) }}</td>
              <td>{{ numberUtils.formatNumberWithCommas(getTransaction(feedType.value).purchased) }}</td>
              <td>{{ numberUtils.formatNumberWithCommas(getTransaction(feedType.value).sold) }}</td>
              <td>{{ numberUtils.formatNumberWithCommas(getTransaction(feedType.value).fed) }}</td>
              <td>{{ numberUtils.formatNumberWithCommas(getTransaction(feedType.value).unexplainedVariance) }}</td>
              <td :class="highlightNegativeError && getTransaction(feedType.value).closingBalance < 0 ? 'g-color-red' : ''"
                  class="g-font-weight-900">{{ numberUtils.formatNumberWithCommas(getTransaction(feedType.value).closingBalance) }}
              </td>
            </tr>
            <tr class="g-font-weight-900">
              <td>Totals</td>
              <td>{{ numberUtils.formatNumberWithCommas(otherFeedTypesTotals.openingBalance) }}</td>
              <td>{{ numberUtils.formatNumberWithCommas(otherFeedTypesTotals.madeOnFarm) }}</td>
              <td>{{ numberUtils.formatNumberWithCommas(otherFeedTypesTotals.purchased) }}</td>
              <td>{{ numberUtils.formatNumberWithCommas(otherFeedTypesTotals.sold) }}</td>
              <td>{{ numberUtils.formatNumberWithCommas(otherFeedTypesTotals.fed) }}</td>
              <td>{{ numberUtils.formatNumberWithCommas(otherFeedTypesTotals.unexplainedVariance) }}</td>
              <td :class="highlightNegativeError && otherFeedTypesTotals.closingBalance < 0 ? 'g-color-red' : ''">
                {{ numberUtils.formatNumberWithCommas(otherFeedTypesTotals.closingBalance) }}
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="row" v-if="highlightNegativeError && isNegativeClosingStock">
      <div class="col-md-12 text-left">
        <!-- Danger Alert -->
        <div class="alert alert-warning text-left" role="alert">
          <p>
            There are negative values in your closing balance.
          </p>
        </div>
        <!-- End Danger Alert -->
      </div>

    </div>
  </div>
</template>

<script>
import NumberUtils from '../../utils/numberutils';
import FeedRecService from '../../services/feedrecservice';
import dateUtils from '../../utils/dateutils';
import {computed, onMounted, ref, toRefs, watch} from 'vue';

export default {
  name: 'SummaryTransactionsList',
  props: {
    farm: String,
    month: Number,
    year: Number,
    highlightNegativeError: Boolean
  },
  emit: ['dataLoaded'],
  setup(props, {emit}) {
    const {farm, month, year} = toRefs(props);

    const transactions = ref([]);
    const feedRecService = FeedRecService;
    const numberUtils = NumberUtils;

    const totalTransactions = ref(Object.assign({}, {
      openingBalance: 0,
      madeOnFarm: 0,
      purchased: 0,
      sold: 0,
      fed: 0,
      unexplainedVariance: 0,
      closingBalance: 0
    }));

    const feedTypeList = ref([
      {text: 'Pit silage', value: 'P'},
      {text: 'Balage', value: 'B'},
      {text: 'Hay', value: 'H'},
      {text: 'Straw', value: 'S'}
    ]);

    // Separate feed types for the two tables
    const pitSilageFeedTypes = computed(() => {
      return feedTypeList.value.filter(feedType => feedType.value === 'P');
    });

    const otherFeedTypes = computed(() => {
      return feedTypeList.value.filter(feedType => feedType.value !== 'P');
    });

    // Calculate totals for pit silage only
    const pitSilageTotals = computed(() => {
      const totals = {
        openingBalance: 0,
        madeOnFarm: 0,
        purchased: 0,
        sold: 0,
        fed: 0,
        unexplainedVariance: 0,
        closingBalance: 0
      };

      pitSilageFeedTypes.value.forEach(feedType => {

        const filteredTransactions = transactions.value.filter(t => t.feedType.trim() === feedType.value.trim());

        filteredTransactions.forEach(transaction => {
          totals.openingBalance += transaction.openingBalance;
          totals.madeOnFarm += transaction.madeOnFarm;
          totals.purchased += transaction.purchased;
          totals.sold += transaction.sold;
          totals.fed += transaction.fed;
          totals.unexplainedVariance += transaction.unexplainedVariance;
          totals.closingBalance += transaction.closingBalance;
        });
      });

      return totals;
    });

    // Calculate totals for other feed types
    const otherFeedTypesTotals = computed(() => {
      const totals = {
        openingBalance: 0,
        madeOnFarm: 0,
        purchased: 0,
        sold: 0,
        fed: 0,
        unexplainedVariance: 0,
        closingBalance: 0
      };

      otherFeedTypes.value.forEach(feedType => {
        const filteredTransactions = transactions.value.filter(t => t.feedType.trim() === feedType.value.trim());
        filteredTransactions.forEach(transaction => {
          totals.openingBalance += transaction.openingBalance;
          totals.madeOnFarm += transaction.madeOnFarm;
          totals.purchased += transaction.purchased;
          totals.sold += transaction.sold;
          totals.fed += transaction.fed;
          totals.unexplainedVariance += transaction.unexplainedVariance;
          totals.closingBalance += transaction.closingBalance;
        });
      });

      return totals;
    });

    const reloadData = async () => {

      transactions.value.splice(0);

      if (farm.value != null && month.value != null) {
        const fullDate = dateUtils.getISOString(new Date(year.value, month.value - 1, 1));
        const results = await feedRecService.fetchSummary(farm.value, fullDate);
        transactions.value.push(...results);

        //calculate the totals of all categories
        results.forEach(summaryTrans => {
          totalTransactions.value.openingBalance += summaryTrans.openingBalance;
          totalTransactions.value.madeOnFarm += summaryTrans.madeOnFarm;
          totalTransactions.value.purchased += summaryTrans.purchased;
          totalTransactions.value.sold += summaryTrans.sold;
          totalTransactions.value.fed += summaryTrans.fed;
          totalTransactions.value.unexplainedVariance += summaryTrans.unexplainedVariance;
          totalTransactions.value.closingBalance += summaryTrans.closingBalance;

        });

        emit("dataLoaded", {transactions: transactions.value, totalTransactions: totalTransactions.value});
      }
    };

    const isNegativeClosingStock = computed(() => {
      return transactions.value.some(f => f.closingBalance < 0) || 
             pitSilageTotals.value.closingBalance < 0 || 
             otherFeedTypesTotals.value.closingBalance < 0;
    });

    onMounted(reloadData);
    watch(farm, reloadData);

    return {
      feedTypeList,
      pitSilageFeedTypes,
      otherFeedTypes,
      pitSilageTotals,
      otherFeedTypesTotals,
      transactions,
      reloadData,
      feedRecService,
      totalTransactions,
      isNegativeClosingStock,
      numberUtils
    }
  },
  data() {
    return {}
  },

  methods: {
    getFeedType(feedType) {
      return this.feedTypeList.find(c => c.value === feedType.trim()).text;
    },
    getTransaction(feedType) {
      let transaction = this.transactions.find(c => c.feedType.trim() === feedType.trim());
      if (!transaction) {
        transaction = Object.assign({}, {
          openingBalance: 0,
          madeOnFarm: 0,
          purchased: 0,
          sold: 0,
          fed: 0,
          closingBalance: 0

        });
      }
      return transaction;
    }
  }
}
</script>